import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Mic, MicOff, Volume2, X, Loader2, Command, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Enhanced Voice command patterns with Command Palette integration
const COMMAND_PATTERNS = [
  // Navigation commands
  { patterns: ["go to dashboard", "open dashboard", "show dashboard", "my dashboard"], action: "navigate", target: "/dashboard" },
  { patterns: ["go to medications", "show medications", "my medications", "open medications"], action: "navigate", target: "/medications" },
  { patterns: ["go to calendar", "show calendar", "open calendar", "my calendar", "care calendar"], action: "navigate", target: "/care-calendar" },
  { patterns: ["go to journal", "open journal", "my journal"], action: "navigate", target: "/journal" },
  { patterns: ["go home", "go to home", "home page"], action: "navigate", target: "/" },
  { patterns: ["ask ai", "chat with ai", "open chat", "talk to ai", "ai chat"], action: "navigate", target: "/chat" },
  { patterns: ["show symptoms", "symptom guide", "symptom help", "symptom management"], action: "navigate", target: "/symptoms" },
  { patterns: ["show resources", "community resources", "local resources"], action: "navigate", target: "/resources" },
  { patterns: ["show memories", "joyful memories", "my memories", "legacy memories"], action: "navigate", target: "/memories" },
  { patterns: ["staff dashboard", "staff portal"], action: "navigate", target: "/staff" },
  { patterns: ["admin dashboard", "admin portal", "administration"], action: "navigate", target: "/admin" },
  { patterns: ["analytics", "show analytics", "reports"], action: "navigate", target: "/analytics" },
  { patterns: ["hospice os", "hos", "operating system"], action: "navigate", target: "/hos" },
  { patterns: ["messages", "my messages", "inbox"], action: "navigate", target: "/messages" },
  { patterns: ["patients", "patient list", "my patients"], action: "navigate", target: "/patients" },
  { patterns: ["scheduling", "smart scheduling", "schedule"], action: "navigate", target: "/smart-scheduling" },
  { patterns: ["telehealth", "video call", "virtual visit"], action: "navigate", target: "/telehealth" },
  { patterns: ["compliance", "compliance hub"], action: "navigate", target: "/compliance-hub" },
  { patterns: ["rn command center", "nursing command center"], action: "navigate", target: "/rn-command-center" },
  { patterns: ["physician portal", "doctor portal"], action: "navigate", target: "/physician-portal" },
  
  // Command Palette integration
  { patterns: ["new visit note", "create visit note", "start documentation", "document visit"], action: "palette", command: "visit_note", label: "Smart Documentation" },
  { patterns: ["new order", "create order", "orders"], action: "palette", command: "orders", label: "Orders" },
  { patterns: ["pain management", "pain assessment", "manage pain"], action: "palette", command: "pain_management", label: "Pain Management" },
  { patterns: ["wound care", "wound assessment"], action: "palette", command: "wound_care", label: "Wound Care" },
  { patterns: ["medication reconciliation", "med rec", "reconcile medications"], action: "palette", command: "medication", label: "Medication Reconciliation" },
  { patterns: ["ai assessment", "clinical ai", "ai help"], action: "palette", command: "ai_assessment", label: "AI Clinical Assessment" },
  { patterns: ["idg meeting", "idg prep", "interdisciplinary"], action: "palette", command: "idg_discussion", label: "IDG Discussion" },
  { patterns: ["dme order", "equipment order", "medical equipment"], action: "palette", command: "dme", label: "DME Orders" },
  { patterns: ["consent form", "consent capture"], action: "palette", command: "consent_forms", label: "Consent Forms" },
  { patterns: ["compliance check", "lcd check", "eligibility check"], action: "palette", command: "compliance_check", label: "Compliance Check" },
  { patterns: ["respiratory care", "breathing assessment"], action: "palette", command: "respiratory", label: "Respiratory Care" },
  { patterns: ["bereavement", "grief support", "bereavement call"], action: "palette", command: "bereavement", label: "Bereavement Support" },
  { patterns: ["quality dashboard", "quality metrics", "qapi"], action: "palette", command: "quality_dashboard", label: "Quality Dashboard" },
  { patterns: ["volunteer management", "volunteers"], action: "palette", command: "volunteer_management", label: "Volunteer Management" },
  
  // Action commands
  { patterns: ["check in", "daily check in", "start check in"], action: "checkin" },
  { patterns: ["voice check in", "start voice check in"], action: "voiceCheckin" },
  { patterns: ["what are my medications", "medications today", "today's medications"], action: "getMedications" },
  { patterns: ["log pain", "record pain"], action: "logPain" },
  { patterns: ["call nurse", "call hospice", "emergency"], action: "callNurse" },
  { patterns: ["help", "what can you do", "commands", "voice commands", "show commands"], action: "help" },
  { patterns: ["open command palette", "command palette", "commands menu", "quick commands"], action: "openPalette" },
  
  // Quick info
  { patterns: ["how am i doing", "my status", "my health"], action: "getStatus" },
  { patterns: ["upcoming appointments", "what's coming up", "my appointments", "next visit"], action: "getUpcoming" },
];

export default function GlobalVoiceCommands() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  
  const recognitionRef = useRef(null);
  const wakeWordDetectedRef = useRef(false);
  const restartTimeoutRef = useRef(null);
  const isActiveRef = useRef(false);

  // Text-to-speech
  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
    setResponse(text);
  }, []);

  // Log pain level helper
  const logPainLevel = useCallback(async (level) => {
    try {
      speak(`Logging pain level ${level}.`);
    } catch (e) {
      speak("Could not log pain level. Please try again.");
    }
  }, [speak]);

  // Execute action based on command
  const executeAction = useCallback(async (action, target, command, paletteCommand = null, paletteLabel = null) => {
    switch (action) {
      case "navigate":
        speak(`Opening ${target.replace("/", "").replace("-", " ")} page.`);
        setTimeout(() => navigate(target), 500);
        break;
      
      case "palette":
        speak(`Opening ${paletteLabel || paletteCommand}.`);
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("voicecommand:palette", {
            detail: { commandId: paletteCommand, label: paletteLabel, source: "voice" }
          }));
        }, 300);
        break;
      
      case "openPalette":
        speak("Opening command palette.");
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
        break;
        
      case "checkin":
        speak("Opening your daily check-in.");
        navigate("/dashboard");
        setTimeout(() => window.dispatchEvent(new CustomEvent('open-checkin')), 1000);
        break;
        
      case "voiceCheckin":
        speak("Starting voice check-in.");
        navigate("/dashboard");
        setTimeout(() => window.dispatchEvent(new CustomEvent('open-voice-checkin')), 1000);
        break;
        
      case "getMedications":
        try {
          const res = await api.get("/medications/today");
          const meds = res.data.schedule || [];
          if (meds.length === 0) {
            speak("You have no medications scheduled for today.");
          } else {
            const taken = meds.filter(m => m.taken).length;
            speak(`You have ${meds.length} medications today. ${taken} taken.`);
          }
        } catch (e) {
          speak("I could not get your medications. Please try again.");
        }
        break;
        
      case "logPain":
        speak("What is your pain level from 0 to 10?");
        break;
        
      case "callNurse":
        speak("Calling the Goodwin Hospice nurse line.");
        setTimeout(() => { window.location.href = "tel:703-578-7108"; }, 1500);
        break;
        
      case "help":
        setShowHelp(true);
        speak("Here are some things you can ask me.");
        break;
        
      case "getStatus":
        try {
          const res = await api.get("/dashboard/quick-stats");
          const stats = res.data;
          const checkedIn = stats?.checkin?.completed_today;
          speak(`You ${checkedIn ? 'have' : "have not"} checked in today.`);
        } catch (e) {
          speak("I could not get your status.");
        }
        break;
        
      case "getUpcoming":
        try {
          const res = await api.get("/calendar/upcoming", { params: { days: 7 } });
          const events = res.data.events || [];
          if (events.length === 0) {
            speak("You have no upcoming events this week.");
          } else {
            speak(`You have ${events.length} upcoming events.`);
          }
        } catch (e) {
          speak("I could not get your upcoming events.");
        }
        break;
        
      default:
        speak("I am not sure how to help with that. Try saying help.");
    }
  }, [speak, navigate]);

  // Process voice command
  const processCommand = useCallback(async (command) => {
    setIsProcessing(true);
    const lowerCommand = command.toLowerCase();
    
    // Find matching command
    for (const cmd of COMMAND_PATTERNS) {
      for (const pattern of cmd.patterns) {
        if (lowerCommand.includes(pattern)) {
          await executeAction(cmd.action, cmd.target, lowerCommand, cmd.command, cmd.label);
          setIsProcessing(false);
          return;
        }
      }
    }
    
    // Check for pain level
    const painMatch = lowerCommand.match(/pain (?:level |is )?(\d+)/);
    if (painMatch) {
      const painLevel = parseInt(painMatch[1]);
      if (painLevel >= 0 && painLevel <= 10) {
        await logPainLevel(painLevel);
        setIsProcessing(false);
        return;
      }
    }
    
    // Fuzzy match
    const fuzzyMatches = [
      { keywords: ["visit", "note", "documentation"], command: "visit_note", label: "Smart Documentation" },
      { keywords: ["order"], command: "orders", label: "Orders" },
      { keywords: ["pain"], command: "pain_management", label: "Pain Management" },
      { keywords: ["wound"], command: "wound_care", label: "Wound Care" },
      { keywords: ["medication", "med"], command: "medication", label: "Medication Reconciliation" },
    ];
    
    for (const match of fuzzyMatches) {
      if (match.keywords.some(kw => lowerCommand.includes(kw))) {
        await executeAction("palette", null, lowerCommand, match.command, match.label);
        setIsProcessing(false);
        return;
      }
    }
    
    speak("I did not quite catch that. Say help for a list of commands.");
    setIsProcessing(false);
  }, [executeAction, logPainLevel, speak]);

  // Initialize speech recognition
  const initRecognition = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setConnectionStatus("connected");
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      
      const fullText = (finalTranscript || interimTranscript).toLowerCase().trim();
      setTranscript(fullText);
      
      if (fullText.includes("hey goodwin") || fullText.includes("okay goodwin") || fullText.includes("goodwin")) {
        wakeWordDetectedRef.current = true;
        setIsActive(true);
        speak("Yes, I am listening. How can I help?");
      }
      
      if (wakeWordDetectedRef.current && finalTranscript) {
        const command = finalTranscript.replace(/hey goodwin|okay goodwin|goodwin/gi, "").trim();
        if (command.length > 2) {
          processCommand(command);
          wakeWordDetectedRef.current = false;
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.log('Voice command error:', event.error);
      
      if (event.error !== 'not-allowed' && event.error !== 'permission-denied') {
        setConnectionStatus("reconnecting");
        
        if (isActiveRef.current) {
          restartTimeoutRef.current = setTimeout(() => {
            if (isActiveRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                // Retry later
              }
            }
          }, 500);
        }
      } else {
        setConnectionStatus("disconnected");
        setIsListening(false);
      }
    };
    
    recognition.onend = () => {
      if (isActiveRef.current) {
        setConnectionStatus("reconnecting");
        restartTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              // Retry
            }
          }
        }, 100);
      } else {
        setConnectionStatus("disconnected");
      }
    };
    
    recognitionRef.current = recognition;
  }, [speak, processCommand]);

  // Setup on mount
  useEffect(() => {
    initRecognition();
    
    return () => {
      isActiveRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    };
  }, [initRecognition]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      isActiveRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
      setIsListening(false);
      setIsActive(false);
      setConnectionStatus("disconnected");
    } else {
      isActiveRef.current = true;
      if (!recognitionRef.current) {
        initRecognition();
      }
      if (recognitionRef.current) {
        try {
          setConnectionStatus("connecting");
          recognitionRef.current.start();
          setIsListening(true);
          toast.info('Voice commands active. Say "Hey Goodwin" followed by a command.');
        } catch (e) {
          toast.error("Could not start voice recognition");
        }
      }
    }
  }, [isListening, initRecognition]);

  if (!user) return null;

  return (
    <>
      {/* Floating Voice Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          size="lg"
          className={`rounded-full w-14 h-14 shadow-lg transition-all ${
            isListening 
              ? connectionStatus === "connected" 
                ? 'bg-green-500 hover:bg-green-600 animate-pulse' 
                : 'bg-yellow-500 hover:bg-yellow-600'
              : ''
          }`}
          onClick={toggleListening}
          data-testid="global-voice-btn"
        >
          {isListening ? (
            connectionStatus === "reconnecting" ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Mic className="w-6 h-6" />
            )
          ) : (
            <MicOff className="w-6 h-6" />
          )}
        </Button>
        
        {isListening && (
          <div className="absolute bottom-16 right-0 bg-card border rounded-lg shadow-lg p-3 w-72">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge variant={isActive ? "default" : "secondary"}>
                  {isActive ? "Listening..." : 'Say "Hey Goodwin"'}
                </Badge>
                {connectionStatus === "connected" && (
                  <Wifi className="w-3 h-3 text-green-500" />
                )}
                {connectionStatus === "reconnecting" && (
                  <WifiOff className="w-3 h-3 text-yellow-500 animate-pulse" />
                )}
              </div>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={toggleListening}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {transcript && (
              <p className="text-sm text-muted-foreground truncate">{transcript}</p>
            )}
            {response && (
              <div className="mt-2 p-2 bg-primary/10 rounded text-sm flex items-start gap-2">
                <Volume2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>{response}</span>
              </div>
            )}
            {isProcessing && (
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </div>
            )}
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Try: &quot;Open command palette&quot; • &quot;New visit note&quot; • &quot;Pain management&quot;
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              Voice Commands
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-96 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Say <strong>&quot;Hey Goodwin&quot;</strong> followed by a command:
            </p>
            
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                  <Command className="w-4 h-4" /> Command Palette Features
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                  <li>• &quot;New visit note&quot; - Smart Documentation</li>
                  <li>• &quot;Pain management&quot; - Pain Assessment</li>
                  <li>• &quot;Wound care&quot; - Wound Assessment</li>
                  <li>• &quot;New order&quot; - Create Orders</li>
                  <li>• &quot;IDG meeting&quot; - IDG Discussion</li>
                  <li>• &quot;Open command palette&quot; - All features</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-1">Navigation</h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                  <li>• &quot;Go to dashboard&quot;</li>
                  <li>• &quot;Staff dashboard&quot;</li>
                  <li>• &quot;Admin dashboard&quot;</li>
                  <li>• &quot;Open medications&quot;</li>
                  <li>• &quot;Care calendar&quot;</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-1">Actions</h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                  <li>• &quot;Check in&quot; - Daily check-in</li>
                  <li>• &quot;Pain level 5&quot; - Log pain</li>
                  <li>• &quot;Call nurse&quot; - Emergency line</li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
